import parse from 'html-react-parser'
import PRICING_DATA from './pricing.json'
import { isPaypayEnabled } from '@/lib/paypay'
import { isAmazonPayEnabled } from '@/lib/amazonpay'

interface Section {
  title: string
  items?: Array<{
    label: string
    text: string
  }>
}

interface ContentType {
  intro?: string
  paragraphs?: string[]
  sections?: Section[]
  list?: string[]
  footer?: string[]
}

interface PricingDataType {
  title: string
  id: string
  content: ContentType
}

const PAYMENT_METHODS_TEXT_KEY =
  '※ お支払い方法は、クレジットカード、PayPay、Amazon Payよりお選びいただけます。'

const buildPaymentMethodsText = (
  showPaypay: boolean,
  showAmazonPay: boolean
): string => {
  const methods = ['クレジットカード']
  if (showPaypay) methods.push('PayPay')
  if (showAmazonPay) methods.push('Amazon Pay')
  return `※ お支払い方法は、${methods.join('、')}よりお選びいただけます。`
}

// PayPay / Amazon Pay 表示可否に応じてPricingデータを変換
const transformPricingData = (data: PricingDataType[]): PricingDataType[] => {
  const showPaypay = isPaypayEnabled()
  const showAmazonPay = isAmazonPayEnabled()

  return data.map((item) => {
    if (item.id !== 'payment-fee' || !item.content.sections) return item
    return {
      ...item,
      content: {
        ...item.content,
        sections: item.content.sections.map((section) => ({
          ...section,
          items: section.items?.map((subItem) => {
            if (subItem.text === PAYMENT_METHODS_TEXT_KEY) {
              return {
                ...subItem,
                text: buildPaymentMethodsText(showPaypay, showAmazonPay),
              }
            }
            return subItem
          }),
        })),
      },
    }
  })
}

const renderContent = (item: PricingDataType) => {
  const { content } = item

  return (
    <div className="space-y-4 text-[13px] text-[#666666] leading-6">
      {content.intro && <p>{parse(content.intro)}</p>}

      {content.sections &&
        content.sections.map((section, idx) => (
          <div key={idx}>
            {section.title && <p className="mb-2">{section.title}</p>}
            {section.items &&
              section.items.map((subItem, subIdx) => (
                <div key={subIdx}>
                  {subItem.label && (
                    <p className="mt-2 font-bold">{subItem.label}</p>
                  )}
                  <p className={subItem.label ? '' : 'mt-2'}>
                    {parse(subItem.text)}
                  </p>
                </div>
              ))}
          </div>
        ))}

      {content.paragraphs &&
        content.paragraphs.map((paragraph, idx) => (
          <p key={idx}>{paragraph}</p>
        ))}

      {content.list && (
        <ul className="list-disc pl-5 space-y-2">
          {content.list.map((listItem, idx) => (
            <li key={idx}>{parse(listItem)}</li>
          ))}
        </ul>
      )}

      {content.footer &&
        content.footer.map((footerItem, idx) => (
          <p key={idx}>{parse(footerItem)}</p>
        ))}
    </div>
  )
}

const Data = () => {
  const transformedData = transformPricingData(
    PRICING_DATA as PricingDataType[]
  )

  return (
    <div>
      {/* Desktop version */}
      <div className="lg:flex flex-col lg:px-2 sm:px-4 px-2 lg:gap-8 gap-4 hidden">
        {transformedData.map((item: PricingDataType, index: number) => (
          <div key={index} className="space-y-4" id={item.id}>
            <h3
              className={`text-[18px] font-normal text-[#000000] ${index === 0 ? 'mt-0' : 'mt-[70px]'} mb-[45px] font-sans`}
            >
              {item.title}
            </h3>
            {renderContent(item)}
          </div>
        ))}
      </div>

      {/* Mobile version */}
      <div className="lg:hidden flex flex-col px-4">
        {transformedData.map((item: PricingDataType, index: number) => (
          <div
            key={index}
            className="space-y-4 py-5 border-b border-[#424242] last:border-b-0"
            id={`${item.id}-mobile`}
          >
            <h3 className="text-[15.6px] font-normal text-[#000000] font-sans">
              {item.title}
            </h3>
            {renderContent(item)}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Data
